/**
 * RoutingPanel — editable chip picker for keywords, places, topics,
 * and sister-site routing.
 *
 * Flow:
 *   1. On mount, GET suggestions for the article (rule + AI).
 *   2. The author ticks the chips they agree with and adds free-form
 *      keywords on top.
 *   3. On save, POST the selected sets to /articles/{id}/routing which
 *      persists them in `article.routing` for the preview-card and
 *      distribution systems.
 *
 * We split chips by `kind` and auto-select everything with
 * `confidence >= 0.7` so a quick "Suggest & save" is a two-click flow.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Sparkles, Loader2, Save, Globe2, Hash, MapPin, Tag,
  Plus, X, Check, AlertCircle,
} from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;
const AUTO_PICK_THRESHOLD = 0.7;


function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}


const KIND_META = {
  keyword: { icon: Hash,   label: "Keywords",     color: "slate" },
  place:   { icon: MapPin, label: "Places",       color: "amber" },
  topic:   { icon: Tag,    label: "Topics",       color: "emerald" },
  site:    { icon: Globe2, label: "Sister sites", color: "blue" },
};


export default function RoutingPanel({ articleId, savedRouting }) {
  const [chips, setChips] = useState([]);
  const [selected, setSelected] = useState({ keyword: new Set(), place: new Set(), topic: new Set(), site: new Set() });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");
  const [aiUsed, setAiUsed] = useState(false);
  const [newChip, setNewChip] = useState({ keyword: "", place: "" });

  // Seed the selected sets from the saved routing on first load so
  // the UI reflects the author's previous accepted picks.
  useEffect(() => {
    if (!savedRouting) return;
    setSelected((prev) => ({
      keyword: new Set((savedRouting.keywords || []).concat([...prev.keyword])),
      place:   new Set((savedRouting.places || []).concat([...prev.place])),
      topic:   new Set((savedRouting.topics || []).concat([...prev.topic])),
      site:    new Set((savedRouting.sites || []).concat([...prev.site])),
    }));
  }, [savedRouting]);

  const refresh = useCallback(async () => {
    if (!articleId) return;
    setLoading(true);
    setError("");
    try {
      const token = getToken();
      const r = await fetch(`${BASE}/articles/${articleId}/routing-suggestions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ use_ai: true }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setChips(j.chips || []);
      setAiUsed(!!j.ai_used);
      // Auto-pick high-confidence chips so the editor only has to
      // uncheck noise, not tick everything.
      setSelected((prev) => {
        const next = { ...prev,
          keyword: new Set(prev.keyword), place: new Set(prev.place),
          topic: new Set(prev.topic), site: new Set(prev.site) };
        for (const c of j.chips || []) {
          if (c.confidence >= AUTO_PICK_THRESHOLD) {
            next[c.kind]?.add(c.value);
          }
        }
        return next;
      });
    } catch (e) {
      setError(`Suggest failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  const toggle = (kind, value) => {
    setSelected((prev) => {
      const s = new Set(prev[kind]);
      if (s.has(value)) s.delete(value); else s.add(value);
      return { ...prev, [kind]: s };
    });
  };

  const addFreeform = (kind) => {
    const v = (newChip[kind] || "").trim();
    if (!v) return;
    setChips((xs) => {
      if (xs.some((c) => c.kind === kind && c.value.toLowerCase() === v.toLowerCase())) return xs;
      return [...xs, { kind, value: v, label: v, confidence: 1.0, source: "manual" }];
    });
    setSelected((prev) => {
      const s = new Set(prev[kind]); s.add(v);
      return { ...prev, [kind]: s };
    });
    setNewChip((prev) => ({ ...prev, [kind]: "" }));
  };

  const save = async () => {
    if (!articleId) return;
    setSaving(true);
    setError("");
    try {
      const token = getToken();
      const payload = {
        keywords: [...selected.keyword],
        places:   [...selected.place],
        topics:   [...selected.topic],
        sites:    [...selected.site],
      };
      const r = await fetch(`${BASE}/articles/${articleId}/routing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      setSavedAt(new Date());
    } catch (e) {
      setError(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const byKind = useMemo(() => {
    const g = { keyword: [], place: [], topic: [], site: [] };
    for (const c of chips) {
      if (g[c.kind]) g[c.kind].push(c);
    }
    return g;
  }, [chips]);

  if (!articleId) {
    return (
      <section className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5 flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Site routing
        </div>
        <div className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3">
          Save the draft first to get keyword & site suggestions.
        </div>
      </section>
    );
  }

  return (
    <section
      className="bg-white border border-slate-200 rounded-2xl p-4"
      data-testid="publisher-write-routing"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Site routing
          {aiUsed && <span className="ml-1 text-[10px] text-emerald-600 font-semibold">AI</span>}
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1"
          data-testid="publisher-write-routing-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {chips.length ? "Re-suggest" : "Suggest chips"}
        </button>
      </div>

      {error && (
        <div className="mb-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 flex items-start gap-1">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError("")} className="text-amber-500"><X className="w-3 h-3" /></button>
        </div>
      )}

      {!loading && chips.length === 0 && (
        <div className="text-xs text-slate-500">
          Click <span className="font-semibold text-blue-600">Suggest chips</span> to let AI + rules pick keywords and sister sites for this article.
        </div>
      )}

      {chips.length > 0 && (
        <>
          {["site", "topic", "place", "keyword"].map((kind) => {
            const list = byKind[kind];
            if (!list?.length) return null;
            const meta = KIND_META[kind];
            const Icon = meta.icon;
            return (
              <div key={kind} className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <Icon className="w-3 h-3" /> {meta.label}
                    <span className="text-slate-400 font-normal">· {[...selected[kind]].length}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {list.map((c) => {
                    const picked = selected[kind]?.has(c.value);
                    const strong = c.confidence >= AUTO_PICK_THRESHOLD;
                    return (
                      <button
                        key={`${kind}-${c.value}`}
                        onClick={() => toggle(kind, c.value)}
                        className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border transition-colors
                          ${picked
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-600 border-slate-200 hover:border-blue-400"}
                          ${strong && !picked ? "ring-1 ring-blue-200" : ""}
                        `}
                        title={(c.reasons || []).join(" · ")}
                        data-testid={`publisher-write-routing-chip-${kind}-${c.value}`}
                      >
                        {picked && <Check className="w-3 h-3" />}
                        <span className="font-semibold">{c.label}</span>
                        <span className={`${picked ? "text-blue-100" : "text-slate-400"} text-[10px]`}>
                          {Math.round(c.confidence * 100)}%
                        </span>
                      </button>
                    );
                  })}
                </div>
                {(kind === "keyword" || kind === "place") && (
                  <div className="mt-2 flex gap-1">
                    <input
                      type="text"
                      value={newChip[kind]}
                      onChange={(e) => setNewChip((p) => ({ ...p, [kind]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFreeform(kind); } }}
                      placeholder={`Add your own ${kind}`}
                      className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1 outline-none focus:border-blue-400"
                      data-testid={`publisher-write-routing-add-${kind}-input`}
                    />
                    <button
                      onClick={() => addFreeform(kind)}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 px-2"
                      data-testid={`publisher-write-routing-add-${kind}-btn`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          <button
            onClick={save}
            disabled={saving}
            className="mt-4 w-full inline-flex items-center justify-center gap-1 h-9 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold disabled:opacity-60"
            data-testid="publisher-write-routing-save"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {saving ? "Saving…" : "Save routing"}
          </button>
          {savedAt && !saving && (
            <div className="mt-1 text-[10px] text-emerald-600 text-center">
              Saved {savedAt.toLocaleTimeString()}
            </div>
          )}
        </>
      )}
    </section>
  );
}
